'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { ButtonShadcn } from '@/components/ui/button-shadcn';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { apiSwaggerClientSide } from '@/shared/api';
import { KeycloakUser, Sample } from '@/sharedTypes/Api';
import { ArrowLeft, X, Plus, Trash2, Users, Music } from 'lucide-react';
import toast from '@/components/ui/custom-toast';
import { useLanguageFromPath } from '@/hooks/useLanguageFromPath';

interface IGroupDetail {
  id: string | null;
  onBack: () => void;
}

interface GroupMember {
  sub: string;
  username: string;
  email?: string;
}

interface AssignedSample {
  _id: string;
  title: string;
  identifier: string;
}

interface GroupState {
  name: string;
  members: GroupMember[];
  assignedSampleIds: Set<string>;
}

const GroupDetail = ({ id, onBack }: IGroupDetail) => {
  const { t } = useTranslation();
  const language = useLanguageFromPath();
  const isCreateMode = !id;

  // Loading states
  const [loading, setLoading] = useState(!isCreateMode);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Initial state (for reset on cancel)
  const [initialState, setInitialState] = useState<GroupState>({
    name: '',
    members: [],
    assignedSampleIds: new Set(),
  });

  // Current editable state
  const [name, setName] = useState('');
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [assignedSamples, setAssignedSamples] = useState<AssignedSample[]>([]);
  const [samplesToAdd, setSamplesToAdd] = useState<Set<string>>(new Set());
  const [samplesToRemove, setSamplesToRemove] = useState<Set<string>>(new Set());

  // Search states
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchedUsers, setSearchedUsers] = useState<KeycloakUser[]>([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);

  const [sampleSearchQuery, setSampleSearchQuery] = useState('');
  const [searchedSamples, setSearchedSamples] = useState<Sample[]>([]);
  const [isSearchingSamples, setIsSearchingSamples] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Fetch group data in edit mode
  const fetchGroupData = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      // Fetch group details
      const groupResponse = await apiSwaggerClientSide.admin.groupsDetail(id);
      if (groupResponse.data?.success && groupResponse.data.data) {
        const group = groupResponse.data.data;
        const groupMembers: GroupMember[] = (group.members || []).map((m) => ({
          sub: m.sub || '',
          username: m.username || '',
          email: m.email,
        }));

        setName(group.name || '');
        setMembers(groupMembers);

        // Fetch samples assigned to this group
        const samplesResponse = await apiSwaggerClientSide.admin.samplesList({
          group: id,
          pageSize: 1000, // Get all samples for this group
        });

        const samples: AssignedSample[] = [];
        if (samplesResponse.data?.success && samplesResponse.data.data?.samples) {
          samplesResponse.data.data.samples.forEach((s) => {
            samples.push({
              _id: s._id,
              title: s.metadata?.title?.[language] || s.metadata?.title?.en || s.identifier,
              identifier: s.identifier,
            });
          });
        }
        setAssignedSamples(samples);

        // Store initial state for cancel reset
        setInitialState({
          name: group.name || '',
          members: groupMembers,
          assignedSampleIds: new Set(samples.map((s) => s._id)),
        });
      }
    } catch (error) {
      console.error('Failed to fetch group:', error);
      toast({
        title: t('adminCatalogue.groupDetail.errorLoadingGroup'),
        description: t('adminCatalogue.groupDetail.errorLoadingGroupDesc'),
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [id, language]);

  useEffect(() => {
    if (!isCreateMode) {
      fetchGroupData();
    }
  }, [isCreateMode, fetchGroupData]);

  // Debounced user search
  useEffect(() => {
    if (userSearchQuery.length < 2) {
      setSearchedUsers([]);
      return;
    }

    const debounce = setTimeout(async () => {
      setIsSearchingUsers(true);
      try {
        const res = await apiSwaggerClientSide.admin.usersSearchList({ q: userSearchQuery });
        if (res.data?.success && res.data.data) {
          // Filter out users already in the group
          const memberSubs = new Set(members.map((m) => m.sub));
          setSearchedUsers(res.data.data.filter((u) => u.sub && !memberSubs.has(u.sub)));
        } else {
          setSearchedUsers([]);
        }
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setIsSearchingUsers(false);
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [userSearchQuery, members]);

  // Debounced sample search
  useEffect(() => {
    if (sampleSearchQuery.length < 2) {
      setSearchedSamples([]);
      return;
    }

    const debounce = setTimeout(async () => {
      setIsSearchingSamples(true);
      try {
        const res = await apiSwaggerClientSide.admin.samplesList({
          search: sampleSearchQuery,
          pageSize: 20,
        });
        if (res.data?.success && res.data.data?.samples) {
          // Filter out samples already assigned (unless marked for removal)
          const assignedIds = new Set(assignedSamples.map((s) => s._id));
          setSearchedSamples(
            res.data.data.samples.filter(
              (s) => !assignedIds.has(s._id) || samplesToRemove.has(s._id)
            )
          );
        } else {
          setSearchedSamples([]);
        }
      } catch (error) {
        console.error('Error searching samples:', error);
      } finally {
        setIsSearchingSamples(false);
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [sampleSearchQuery, assignedSamples, samplesToRemove]);

  const handleAddUser = (user: KeycloakUser) => {
    if (!user.sub || !user.username) return;
    setMembers((prev) => [...prev, { sub: user.sub!, username: user.username!, email: user.email }]);
    setUserSearchQuery('');
    setSearchedUsers([]);
  };

  const handleRemoveUser = (sub: string) => {
    setMembers((prev) => prev.filter((m) => m.sub !== sub));
  };

  const handleAssignSample = (sample: Sample) => {
    const newSample: AssignedSample = {
      _id: sample._id,
      title: sample.metadata?.title?.[language] || sample.metadata?.title?.en || sample.identifier,
      identifier: sample.identifier,
    };

    // If it was previously assigned and marked for removal, just unmark it
    if (samplesToRemove.has(sample._id)) {
      setSamplesToRemove((prev) => {
        const next = new Set(prev);
        next.delete(sample._id);
        return next;
      });
    } else {
      // Add to samples to add
      setSamplesToAdd((prev) => new Set(prev).add(sample._id));
      setAssignedSamples((prev) => [...prev, newSample]);
    }

    setSampleSearchQuery('');
    setSearchedSamples([]);
  };

  const handleUnassignSample = (sampleId: string) => {
    // If it was newly added, just remove it from the add list
    if (samplesToAdd.has(sampleId)) {
      setSamplesToAdd((prev) => {
        const next = new Set(prev);
        next.delete(sampleId);
        return next;
      });
      setAssignedSamples((prev) => prev.filter((s) => s._id !== sampleId));
    } else {
      // Mark for removal
      setSamplesToRemove((prev) => new Set(prev).add(sampleId));
    }
  };

  const handleCancel = () => {
    // Reset to initial state
    setName(initialState.name);
    setMembers(initialState.members);
    setAssignedSamples(
      Array.from(initialState.assignedSampleIds).map((id) => {
        const existing = assignedSamples.find((s) => s._id === id);
        return existing || { _id: id, title: id, identifier: id };
      })
    );
    setSamplesToAdd(new Set());
    setSamplesToRemove(new Set());
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast({
        title: t('adminCatalogue.groupDetail.nameRequired'),
        description: t('adminCatalogue.groupDetail.nameRequiredDesc'),
        type: 'error',
      });
      return;
    }

    setIsSaving(true);
    try {
      let groupId = id;

      if (isCreateMode) {
        // Create new group
        const createResponse = await apiSwaggerClientSide.admin.groupsCreate({
          name: name.trim(),
          members: members.map((m) => m.sub),
        });

        if (createResponse.data?.success && createResponse.data.data?._id) {
          groupId = createResponse.data.data._id;
          toast({
            title: t('adminCatalogue.groupDetail.groupCreated'),
            description: t('adminCatalogue.groupDetail.groupCreatedDesc', { name }),
            type: 'success',
          });
        } else {
          throw new Error('Failed to create group');
        }
      } else {
        // Update existing group
        await apiSwaggerClientSide.admin.groupsPartialUpdate(id!, {
          name: name.trim(),
          members: members.map((m) => m.sub),
        });
      }

      // Update sample assignments
      if (groupId) {
        // Add group to new samples
        if (samplesToAdd.size > 0) {
          await apiSwaggerClientSide.admin.samplesBulkCreate({
            sampleIds: Array.from(samplesToAdd),
            groups: [groupId],
          });
        }

        // Remove group from samples
        if (samplesToRemove.size > 0) {
          await apiSwaggerClientSide.admin.samplesBulkCreate({
            sampleIds: Array.from(samplesToRemove),
            groupsToRemove: [groupId],
          });
        }
      }

      if (!isCreateMode) {
        toast({
          title: t('adminCatalogue.groupDetail.groupUpdated'),
          description: t('adminCatalogue.groupDetail.groupUpdatedDesc', { name }),
          type: 'success',
        });
      }

      onBack();
    } catch (error) {
      console.error('Failed to save group:', error);
      toast({
        title: t('adminCatalogue.groupDetail.errorSavingGroup'),
        description: t('adminCatalogue.groupDetail.errorSavingGroupDesc'),
        type: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    setIsDeleting(true);
    try {
      const response = await apiSwaggerClientSide.admin.groupsDelete(id);
      if (response.data?.success) {
        toast({
          title: t('adminCatalogue.groups.groupDeleted'),
          description: t('adminCatalogue.groups.groupDeletedDesc', { name }),
          type: 'success',
        });
        onBack();
      }
    } catch (error) {
      console.error('Failed to delete group:', error);
      toast({
        title: t('adminCatalogue.groups.errorDeletingGroup'),
        description: t('adminCatalogue.groups.errorDeletingGroupDesc'),
        type: 'error',
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full gap-4">
        {/* Header skeleton */}
        <div className="flex items-center gap-4 pb-4 border-b">
          <Skeleton className="h-10 w-10 rounded" />
          <Skeleton className="h-10 w-64" />
        </div>
        {/* Content skeleton */}
        <div className="flex gap-6 flex-1">
          <div className="flex-[4] flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5" />
              <Skeleton className="h-5 w-32" />
            </div>
            <Skeleton className="h-10 w-full" />
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-lg" />
              ))}
            </div>
          </div>
          <div className="flex-[6] flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5" />
              <Skeleton className="h-5 w-32" />
            </div>
            <Skeleton className="h-10 w-full" />
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-lg" />
              ))}
            </div>
          </div>
        </div>
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  // Get visible assigned samples (excluding those marked for removal)
  const visibleAssignedSamples = assignedSamples.filter((s) => !samplesToRemove.has(s._id));

  return (
    <div className="flex flex-col h-full" data-testid="group-detail-view">
      {/* Header with editable name */}
      <div className="flex items-center gap-4 mb-4 pb-4 border-b">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-md transition-colors border-0 bg-transparent cursor-pointer"
          data-testid="group-detail-back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <Input
          type="text"
          placeholder={t('adminCatalogue.groupDetail.enterGroupName')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="text-lg font-semibold flex-1 max-w-md"
          data-testid="group-detail-name-input"
        />
      </div>

      {/* Two-column content */}
      <div className="flex gap-6 flex-1 min-h-0 overflow-hidden">
        {/* Left column - Manage Users (40%) */}
        <div className="flex-[4] flex flex-col border rounded-lg p-4 overflow-hidden">
          <div className="flex items-center gap-2 text-gray-700 mb-3">
            <Users className="h-5 w-5" />
            <span className="font-semibold">
              {t('adminCatalogue.groupDetail.membersCount', { count: members.length })}
            </span>
          </div>

          {/* User search */}
          <div className="mb-3 relative">
            <Input
              type="text"
              placeholder={t('adminCatalogue.groupDetail.searchUsersToAdd')}
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              data-testid="group-detail-user-search"
            />
            {/* Search results dropdown */}
            {(searchedUsers.length > 0 || isSearchingUsers) && (
              <div className="absolute top-full left-0 right-0 mt-1 border rounded-lg bg-white shadow-lg max-h-48 overflow-y-auto z-20">
                {isSearchingUsers ? (
                  <div className="p-3 text-sm text-gray-500">{t('adminCatalogue.common.searching')}</div>
                ) : (
                  searchedUsers.map((user) => (
                    <button
                      key={user.sub}
                      onClick={() => handleAddUser(user)}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 border-0 bg-transparent cursor-pointer text-sm flex items-center gap-3 transition-colors"
                    >
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-medium text-xs">
                        {user.username?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">{user.username}</span>
                        {user.email && (
                          <span className="text-xs text-gray-500">{user.email}</span>
                        )}
                      </div>
                      <Plus className="h-4 w-4 ml-auto text-green-600" />
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Current members list */}
          <div className="flex-1 overflow-y-auto">
            {members.length === 0 ? (
              <div className="flex items-center justify-center h-24 border rounded-lg bg-gray-50">
                <span className="text-gray-500 text-sm">{t('adminCatalogue.groupDetail.noMembersYet')}</span>
              </div>
            ) : (
              <div className="space-y-2">
                {members.map((member) => (
                  <div
                    key={member.sub}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    data-testid={`group-member-${member.sub}`}
                  >
                    <div className="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-medium text-sm">
                      {member.username?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900 truncate">{member.username}</div>
                      {member.email && (
                        <div className="text-xs text-gray-500 truncate">{member.email}</div>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveUser(member.sub)}
                      className="p-1.5 hover:bg-red-100 rounded-full text-red-500 hover:text-red-600 border-0 bg-transparent cursor-pointer transition-colors"
                      data-testid={`group-member-remove-${member.sub}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right column - Assign Pieces (60%) */}
        <div className="flex-[6] flex flex-col border rounded-lg p-4 overflow-hidden">
          <div className="flex items-center gap-2 text-gray-700 mb-3">
            <Music className="h-5 w-5" />
            <span className="font-semibold">{t('adminCatalogue.groupDetail.assignedMusicSheets')} ({visibleAssignedSamples.length})</span>
          </div>

          {/* Sample search */}
          <div className="mb-3 relative">
            <Input
              type="text"
              placeholder={t('adminCatalogue.groupDetail.searchMusicSheetsToAssign')}
              value={sampleSearchQuery}
              onChange={(e) => setSampleSearchQuery(e.target.value)}
              data-testid="group-detail-sample-search"
            />
            {/* Search results dropdown */}
            {(searchedSamples.length > 0 || isSearchingSamples) && (
              <div className="absolute top-full left-0 right-0 mt-1 border rounded-lg bg-white shadow-lg max-h-48 overflow-y-auto z-20">
                {isSearchingSamples ? (
                  <div className="p-3 text-sm text-gray-500">{t('adminCatalogue.common.searching')}</div>
                ) : (
                  searchedSamples.map((sample) => (
                    <button
                      key={sample._id}
                      onClick={() => handleAssignSample(sample)}
                      className="w-full text-left px-3 py-2 hover:bg-green-50 border-0 bg-transparent cursor-pointer text-sm flex items-center gap-3 transition-colors"
                    >
                      <div className="h-8 w-8 rounded bg-green-100 flex items-center justify-center">
                        <Music className="h-4 w-4 text-green-700" />
                      </div>
                      <span className="flex-1 truncate text-gray-900">
                        {sample.metadata?.title?.[language] ||
                          sample.metadata?.title?.en ||
                          sample.identifier}
                      </span>
                      <Plus className="h-4 w-4 text-green-600" />
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Assigned samples list */}
          <div className="flex-1 overflow-y-auto">
            {visibleAssignedSamples.length === 0 ? (
              <div className="flex items-center justify-center h-24 border rounded-lg bg-gray-50">
                <span className="text-gray-500 text-sm">{t('adminCatalogue.groupDetail.noMusicSheetsAssigned')}</span>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                {visibleAssignedSamples.map((sample, index) => {
                  const isNewlyAdded = samplesToAdd.has(sample._id);
                  return (
                    <div
                      key={sample._id}
                      className={`flex items-center gap-3 px-4 py-3 border-b last:border-b-0 hover:bg-gray-50 transition-colors ${
                        isNewlyAdded ? 'bg-green-50' : ''
                      }`}
                    >
                      <span className="w-6 h-6 rounded bg-green-100 text-green-700 flex items-center justify-center text-xs font-medium">
                        {index + 1}
                      </span>
                      <span className="flex-1 text-sm text-gray-700 truncate">{sample.title}</span>
                      {isNewlyAdded && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">{t('adminCatalogue.groupDetail.new')}</span>
                      )}
                      <button
                        onClick={() => handleUnassignSample(sample._id)}
                        className="p-1.5 hover:bg-red-100 rounded-full text-red-500 hover:text-red-600 border-0 bg-transparent cursor-pointer transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-between items-center pt-4 mt-4 border-t">
        <div>
          {!isCreateMode && (
            <ButtonShadcn
              variant="outline"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={isSaving || isDeleting}
              data-testid="group-detail-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t('adminCatalogue.groups.deleteGroup')}
            </ButtonShadcn>
          )}
        </div>
        <div className="flex gap-2">
          <ButtonShadcn variant="outline" onClick={handleCancel} disabled={isSaving || isDeleting} data-testid="group-detail-cancel-btn">
            {t('adminCatalogue.common.cancel')}
          </ButtonShadcn>
          <ButtonShadcn variant="default" onClick={handleSave} disabled={isSaving || isDeleting} data-testid="group-detail-save-btn">
            {isSaving ? t('adminCatalogue.common.saving') : isCreateMode ? t('adminCatalogue.groups.createGroup') : t('adminCatalogue.accessControl.saveChanges')}
          </ButtonShadcn>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('adminCatalogue.groups.deleteGroup')}</DialogTitle>
            <DialogDescription>
              {t('adminCatalogue.groups.deleteGroupConfirm', { name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <ButtonShadcn
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
              data-testid="group-detail-delete-cancel-btn"
            >
              {t('adminCatalogue.common.cancel')}
            </ButtonShadcn>
            <ButtonShadcn
              variant="default"
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDelete}
              disabled={isDeleting}
              data-testid="group-detail-delete-confirm-btn"
            >
              {isDeleting ? t('adminCatalogue.common.deleting') : t('adminCatalogue.groups.deleteGroup')}
            </ButtonShadcn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupDetail;
